/**
 * WizardConfirmationScreen Component (Web Version)
 * Step 4: Final confirmation with booking summary, client notes, and booking submission
 * Features: Mobile-exact layout, booking validation, success screen, Brazilian formatting
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Calendar, Clock, User, CreditCard, MessageSquare, CheckCircle, Loader2, Users } from 'lucide-react';
import { useWizardStore } from '../../stores/wizardStore';
import { useAuthStore } from '../../stores/authStore';
import useServicesStore from '../../stores/servicesStore';
import { buildBookingPayloadV2, createAppointment } from '../../services/wizardApiService';
import { createClient } from '../../services/clientService';
import { useNavigation } from '../../shared/hooks/useNavigation';
import { ROUTES } from '../../shared/navigation';

const WizardConfirmationScreen = ({ onAppointmentCreated }) => {
  const { navigate } = useNavigation();
  const [clientNotes, setClientNotes] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmedBookingData, setConfirmedBookingData] = useState(null);
  const [error, setError] = useState(null);

  const {
    clientData,
    selectedServices,
    selectedDate,
    selectedSlot,
    serviceAssignments,
    availableProfessionals,
    setCurrentStep,
    goToPreviousStep,
    resetWizard,
    mode,
    isClientMode,
  } = useWizardStore();

  const { user, isGuestUser } = useAuthStore();
  const { clearServices } = useServicesStore();

  // Set current step on mount (mobile behavior)
  useEffect(() => {
    setCurrentStep(6);
  }, [setCurrentStep, selectedServices, selectedDate, selectedSlot, user]);

  const maxNotesLength = 500;

  // Format date (Brazilian format - mobile exact)
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString + 'T00:00:00');
      return date.toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: '2-digit', // Mobile uses '2-digit' not 'numeric'
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Format time (Brazilian 24-hour format)
  const formatTime = (timeString) => {
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    } catch (error) {
      return timeString;
    }
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (minutes === undefined || minutes === null || minutes <= 0) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : ''}`.trim() || '-';
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Get professional name by ID
  const getProfessionalName = (professionalId) => {
    const professional = availableProfessionals?.find(p => p.id === professionalId);
    if (professional) {
      return professional.full_name || professional.email || 'Profissional';
    }
    return 'Profissional';
  };

  // Calculate service times for display - uses selectedSlot.services from v2 API
  // The v2 API provides pre-calculated start_time/end_time for each service
  // Also enriches with variation info from selectedServices
  const calculateServiceTimes = () => {
    // If selectedSlot has services array (from v2 transform), use it
    if (selectedSlot?.services?.length > 0) {
      return selectedSlot.services.map((service, index) => {
        // Use timing from v2 API response - backend calculates correct sequential/parallel timing
        const startTime = service.start_time
          ? service.start_time.substring(0, 5) // HH:MM format
          : selectedSlot.start_time?.substring(0, 5) || '--:--';

        // Get variation info from selectedServices by matching service_id or index
        const selectedService = selectedServices.find(s => s.id === service.service_id) || selectedServices[index];
        const variationName = selectedService?.selectedVariation?.name;
        const hasVariation = variationName && variationName !== 'Padrão';

        return {
          ...service,
          calculated_start_time: startTime,
          // Ensure service_name is available
          service_name: service.service_name || service.name,
          professional_name: service.professional_name || getProfessionalName(service.professional_id),
          // Variation info
          variation_name: hasVariation ? variationName : null,
          variation_id: selectedService?.selectedVariation?.id || selectedService?.variation_id || null
        };
      });
    }

    // Fallback: build services from selectedServices and serviceAssignments
    // This is used when slot.services is not populated (shouldn't happen with v2 API)
    if (selectedServices?.length > 0 && serviceAssignments) {
      return selectedServices.map((service, index) => {
        const professionalId = serviceAssignments[String(index)];
        const variationName = service.selectedVariation?.name;
        const hasVariation = variationName && variationName !== 'Padrão';

        return {
          service_id: service.id,
          service_name: service.name,
          duration_minutes: service.duration_minutes,
          price: service.price,
          professional_id: professionalId,
          professional_name: getProfessionalName(professionalId),
          // Use slot start time as we don't have individual timing
          calculated_start_time: selectedSlot?.start_time?.substring(0, 5) || '--:--',
          // Variation info
          variation_name: hasVariation ? variationName : null,
          variation_id: service.selectedVariation?.id || service.variation_id || null
        };
      });
    }

    return [];
  };

  // Handle booking confirmation
  const handleConfirmBooking = useCallback(async () => {
    if (!selectedSlot) {
      alert('Erro: Nenhum horário selecionado.');
      return;
    }

    if (!clientData.id && !clientData.name.trim()) {
      alert('Erro: Nenhum cliente selecionado.');
      return;
    }

    if (!user?.id) {
      alert('Erro: Usuário não autenticado.');
      return;
    }

    // Validate all services have professional assignments
    const assignmentCount = Object.keys(serviceAssignments || {}).length;
    if (assignmentCount !== selectedServices.length) {
      alert('Erro: Nem todos os serviços têm profissionais atribuídos.');
      return;
    }

    setIsBooking(true);
    setError(null);

    try {
      let clientId = clientData.id;

      // Create new client if needed
      if (!clientId && clientData.isNewClient && clientData.name.trim()) {

        const newClientData = {
          full_name: clientData.name.trim(),
          nickname: clientData.nickname?.trim() || null,
          phone: clientData.phone?.trim() || null,
          email: clientData.email?.trim() || null,
          cpf: clientData.cpf?.replace(/\D/g, '') || null,
          address_cep: clientData.address_cep?.replace(/\D/g, '') || null,
          address_street: clientData.address_street?.trim() || null,
          address_number: clientData.address_number?.trim() || null,
          address_complement: clientData.address_complement?.trim() || null,
          address_neighborhood: clientData.address_neighborhood?.trim() || null,
          address_city: clientData.address_city?.trim() || null,
          address_state: clientData.address_state?.trim() || null,
        };

        const newClient = await createClient(newClientData);
        clientId = newClient.id;
      }

      if (!clientId) {
        alert('Erro: Não foi possível identificar o cliente.');
        return;
      }

      // Build client object for the /appointments/scheduled endpoint
      const clientPayload = {
        id: clientId,
        name: clientData.name || '',
        nickname: clientData.nickname || null,
        phone: clientData.phone || null,
        email: clientData.email || null,
        cpf: clientData.cpf || null,
        address_street: clientData.address_street || null,
        address_number: clientData.address_number || null,
        address_complement: clientData.address_complement || null,
        address_neighborhood: clientData.address_neighborhood || null,
        address_city: clientData.address_city || null,
        address_state: clientData.address_state || null,
        address_cep: clientData.address_cep || null
      };

      // Build booking payload matching Web-admin structure for /appointments/scheduled
      const bookingRequest = buildBookingPayloadV2(
        clientPayload,
        selectedServices,
        selectedSlot,
        selectedDate,
        serviceAssignments,
        clientNotes.trim() || null
      );

      console.log('[WizardConfirmationScreen] Booking request:', bookingRequest);

      // Create appointment using /appointments/scheduled API (same as Web-admin)
      const result = await createAppointment(bookingRequest);

      // Store booking data before clearing wizard state
      setConfirmedBookingData({
        selectedServices,
        selectedDate,
        selectedSlot,
      });
      
      // Show success screen (keep wizard state until user clicks "Concluir")
      setBookingConfirmed(true);
      
      // Clear the original service cart/checkout (mobile behavior)
      clearServices();
      
      // Clear local state
      setClientNotes('');
      
      // Call the callback to refresh agenda data
      if (onAppointmentCreated) {
        onAppointmentCreated();
      }
      
    } catch (error) {
      
      // Mobile uses Alert.alert for booking errors - make it prominent
      alert('Erro no Agendamento\n\nNão foi possível confirmar o agendamento. Tente novamente.');
    } finally {
      setIsBooking(false);
    }
  }, [selectedSlot, user, selectedDate, clientNotes, selectedServices, clientData, serviceAssignments, resetWizard, clearServices, onAppointmentCreated]);

  // Get unique professional count
  const getUniqueProfessionalCount = () => {
    // Try to get from selectedSlot.services first
    if (selectedSlot?.services?.length > 0) {
      const uniqueProfessionals = new Set(selectedSlot.services.map(s => s.professional_id));
      return uniqueProfessionals.size;
    }
    // Fallback to serviceAssignments
    if (serviceAssignments) {
      const uniqueProfessionals = new Set(Object.values(serviceAssignments));
      return uniqueProfessionals.size;
    }
    return 0;
  };

  // Render client summary card - Luxury Design
  const renderClientCard = () => (
    <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 mb-4">
      <div className="flex items-center p-5">
        <div className="w-6 h-6 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mr-3">
          <span className="text-sm">👤</span>
        </div>
        <div className="flex-1">
          <h3 className="font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide">Cliente</h3>
          <p className="font-medium text-brand-dark dark:text-brand-light mb-2 tracking-wide">
            {clientData.name || 'Cliente não identificado'}
          </p>
          <div className="space-y-1">
            {clientData.phone && (
              <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">{clientData.phone}</p>
            )}
            {clientData.email && (
              <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">{clientData.email}</p>
            )}
            {clientData.isNewClient && (
              <div className="inline-block bg-transparent border border-green-600/30 dark:border-green-400/30 px-2 py-1">
                <span className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">Novo Cliente</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render date/time summary card - Luxury Design
  const renderDateTimeCard = () => {
    // Calculate total duration from services if not provided
    const totalDuration = selectedSlot?.total_duration_minutes ||
      selectedServices.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);

    // Calculate end time if not provided
    const endTime = selectedSlot?.end_time || (() => {
      if (!selectedSlot?.start_time) return '';
      const start = new Date(`1970-01-01T${selectedSlot.start_time}`);
      start.setMinutes(start.getMinutes() + totalDuration);
      return start.toTimeString().substring(0, 5);
    })();

    return (
      <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 mb-4">
        <div className="flex items-center p-5">
          <div className="w-6 h-6 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mr-3">
            <span className="text-sm">📅</span>
          </div>
          <div className="flex-1">
            <p className="font-medium text-brand-dark dark:text-brand-light text-[15px] capitalize mb-2 tracking-wide">
              {formatDate(selectedDate)}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                {formatTime(selectedSlot?.start_time || '')} - {formatTime(endTime)}
              </span>
              <div className="bg-transparent border border-brand-dark/20 dark:border-brand-light/20 px-2 py-1">
                <span className="text-xs font-medium text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider">
                  {formatDuration(totalDuration)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render services summary - Luxury Design
  const renderServicesCard = () => {
    const servicesWithTimes = calculateServiceTimes();
    const professionalCount = getUniqueProfessionalCount();

    return (
      <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 mb-4">
        {/* Header */}
        <div className="flex items-center px-5 pt-5 pb-3">
          <div className="w-6 h-6 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mr-3">
            <span className="text-sm">💼</span>
          </div>
          <h3 className="font-serif font-bold text-brand-dark dark:text-brand-light flex-1 tracking-wide">Serviços</h3>
          {professionalCount > 1 && (
            <div className="bg-transparent border border-brand-dark/20 dark:border-brand-light/20 px-2 py-1">
              <span className="text-xs font-medium text-brand-dark/60 dark:text-brand-light/60">{professionalCount} prof.</span>
            </div>
          )}
        </div>

        {/* Services content */}
        <div className="px-5 pb-5">
          {servicesWithTimes.length > 0 ? (
            servicesWithTimes.map((service, index) => (
              <div key={index} className="flex items-center justify-between py-4 px-4 mb-3 bg-brand-dark/5 dark:bg-brand-light/5 border border-brand-dark/10 dark:border-brand-light/10">
                <div className="flex-1 mr-3">
                  <h4 className="font-medium text-brand-dark dark:text-brand-light mb-1 tracking-wide">{service.service_name}</h4>
                  {service.variation_name && (
                    <span className="inline-block text-xs bg-brand-dark/10 dark:bg-brand-light/10 text-brand-dark dark:text-brand-light border border-brand-dark/30 dark:border-brand-light/30 px-2 py-0.5 mb-1 tracking-wide">
                      {service.variation_name}
                    </span>
                  )}
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
                      {formatDuration(service.duration_minutes)}
                    </span>
                    <span className="text-sm font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                      {formatPrice(parseFloat(service.price))}
                    </span>
                  </div>
                </div>

                {/* Professional assignment with avatar - Square */}
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-brand-dark dark:bg-brand-light flex items-center justify-center">
                    <span className="text-sm font-semibold text-brand-light dark:text-brand-dark">
                      {(service.professional_name || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-brand-dark/70 dark:text-brand-light/70 max-w-[100px] truncate tracking-wide">
                    {service.professional_name}
                  </span>
                </div>
              </div>
            ))
          ) : (
            // Fallback for when service assignments aren't available (mobile behavior)
            selectedServices.map((service, index) => {
              const variationName = service.selectedVariation?.name;
              const hasVariation = variationName && variationName !== 'Padrão';
              return (
                <div key={index} className="flex items-center justify-between py-4 px-4 mb-3 bg-brand-dark/5 dark:bg-brand-light/5 border border-brand-dark/10 dark:border-brand-light/10">
                  <div className="flex-1 mr-3">
                    <h4 className="font-medium text-brand-dark dark:text-brand-light mb-1 tracking-wide">{service.name}</h4>
                    {hasVariation && (
                      <span className="inline-block text-xs bg-brand-dark/10 dark:bg-brand-light/10 text-brand-dark dark:text-brand-light border border-brand-dark/30 dark:border-brand-light/30 px-2 py-0.5 mb-1 tracking-wide">
                        {variationName}
                      </span>
                    )}
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
                        {formatDuration(service.duration_minutes)}
                      </span>
                      <span className="text-sm font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                        {formatPrice(parseFloat(service.price))}
                      </span>
                    </div>
                  </div>

                  {/* Fallback professional assignment */}
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
                      Atribuição automática
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // Render client notes section - Luxury Design
  const renderClientNotesCard = () => (
    <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 mb-4">
      <div className="flex items-center px-5 pt-5 pb-3">
        <div className="w-6 h-6 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mr-3">
          <span className="text-sm">📝</span>
        </div>
        <h3 className="font-serif font-bold text-brand-dark dark:text-brand-light flex-1 tracking-wide">Observações</h3>
        <span className="text-xs text-brand-dark/50 dark:text-brand-light/50 italic">(opcional)</span>
      </div>

      <div className="px-5 pb-5">
        <textarea
          value={clientNotes}
          onChange={(e) => setClientNotes(e.target.value)}
          placeholder="Alergias, preferências ou observações especiais..."
          maxLength={maxNotesLength}
          className="w-full h-[70px] px-3 py-2 border border-brand-dark/20 dark:border-brand-light/20 resize-none text-sm bg-transparent text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light focus:border-brand-dark dark:focus:border-brand-light tracking-wide"
        />
        <span className="text-xs text-brand-dark/50 dark:text-brand-light/50 float-right mt-1">
          {clientNotes.length}/{maxNotesLength}
        </span>
      </div>
    </div>
  );

  // Render important information card - Luxury Design
  const renderImportantInfoCard = () => (
    <div className="bg-transparent border border-brand-dark/20 dark:border-brand-light/20 mb-6">
      <div className="flex items-center px-5 pt-5 pb-4">
        <div className="w-8 h-8 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mr-3">
          <span className="text-base">ℹ️</span>
        </div>
        <h3 className="font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">Informações Importantes</h3>
      </div>

      <div className="px-5 pb-5 space-y-3">
        <div className="flex items-start space-x-3">
          <span className="text-base mt-0.5">🕐</span>
          <span className="text-sm text-brand-dark/70 dark:text-brand-light/70 leading-5 tracking-wide">Chegue com 10 minutos de antecedência</span>
        </div>

        <div className="flex items-start space-x-3">
          <span className="text-base mt-0.5">📞</span>
          <span className="text-sm text-brand-dark/70 dark:text-brand-light/70 leading-5 tracking-wide">Cancelamentos com 24h de antecedência</span>
        </div>

        <div className="flex items-start space-x-3">
          <span className="text-base mt-0.5">⏰</span>
          <span className="text-sm text-brand-dark/70 dark:text-brand-light/70 leading-5 tracking-wide">Atrasos de 15+ min podem ser reagendados</span>
        </div>
      </div>
    </div>
  );


  // Render booking button - Luxury Design
  const renderBookingButton = () => {
    // Calculate total price from services if not provided
    const totalPrice = selectedSlot?.total_price ||
      selectedServices.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);

    return (
      <div className="fixed bottom-16 left-0 right-0 bg-brand-light dark:bg-brand-dark border-t border-brand-dark/10 dark:border-brand-light/10 p-6">
        <button
          onClick={handleConfirmBooking}
          disabled={isBooking}
          className={`w-full py-5 px-6 border font-medium transition-all duration-300 flex items-center justify-center ${
            isBooking
              ? 'bg-brand-dark/10 dark:bg-brand-light/10 text-brand-dark/40 dark:text-brand-light/40 border-brand-dark/10 dark:border-brand-light/10 cursor-not-allowed'
              : 'bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border-brand-dark dark:border-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90'
          }`}
        >
          {isBooking ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-brand-dark/40 dark:border-brand-light/40 border-t-transparent animate-spin rounded-full"></div>
              <span className="font-medium uppercase tracking-widest text-sm">Confirmando...</span>
            </div>
          ) : (
            <div className="text-center">
              <div className="font-medium uppercase tracking-widest text-sm">Confirmar Agendamento</div>
              <div className="text-xl font-serif font-bold tracking-wide mt-1">{formatPrice(totalPrice)}</div>
            </div>
          )}
        </button>
      </div>
    );
  };

  // Render success screen - Luxury Design
  const renderSuccessScreen = () => {
    // Calculate values for confirmed booking
    const totalDuration = confirmedBookingData?.selectedSlot?.total_duration_minutes ||
      confirmedBookingData?.selectedServices?.reduce((sum, s) => sum + (s.duration_minutes || 0), 0) || 0;

    const endTime = confirmedBookingData?.selectedSlot?.end_time || (() => {
      if (!confirmedBookingData?.selectedSlot?.start_time) return '';
      const start = new Date(`1970-01-01T${confirmedBookingData.selectedSlot.start_time}`);
      start.setMinutes(start.getMinutes() + totalDuration);
      return start.toTimeString().substring(0, 5);
    })();

    const totalPrice = confirmedBookingData?.selectedSlot?.total_price ||
      confirmedBookingData?.selectedServices?.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0) || 0;

    return (
      <div className="min-h-screen h-full overflow-y-auto bg-brand-light dark:bg-brand-dark relative">
        <div className="flex flex-col px-6 pt-6 pb-40 min-h-screen">
          {/* Success header */}
          <div className="text-center mb-6 mt-8">
            <div className="w-20 h-20 bg-brand-dark dark:bg-brand-light border border-brand-dark dark:border-brand-light flex items-center justify-center mb-6 mx-auto">
              <span className="text-4xl text-brand-light dark:text-brand-dark">✓</span>
            </div>
            <h2 className="text-[28px] font-serif font-bold text-brand-dark dark:text-brand-light mb-2 leading-8 tracking-wide">
              Agendamento Confirmado!
            </h2>
          </div>

          {/* Booking summary card - Luxury Design */}
          {confirmedBookingData && (
            <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-5 mb-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">📅 Data</span>
                  <span className="text-sm font-medium text-brand-dark dark:text-brand-light text-right capitalize tracking-wide">
                    {formatDate(confirmedBookingData.selectedDate)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">🕐 Horário</span>
                  <span className="text-sm font-medium text-brand-dark dark:text-brand-light tracking-wide">
                    {formatTime(confirmedBookingData.selectedSlot?.start_time || '')} - {formatTime(endTime)}
                  </span>
                </div>

                <div className="space-y-2">
                  <span className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">💼 Serviços</span>
                  <div className="flex flex-wrap gap-2">
                    {confirmedBookingData.selectedServices.map((service, index) => {
                      const variationName = service.selectedVariation?.name;
                      const hasVariation = variationName && variationName !== 'Padrão';
                      const displayName = hasVariation ? `${service.name} - ${variationName}` : service.name;
                      return (
                        <div key={index} className="bg-transparent border border-brand-dark dark:border-brand-light px-3 py-1.5">
                          <span className="text-xs font-medium text-brand-dark dark:text-brand-light uppercase tracking-wider">{displayName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="h-px bg-brand-dark/10 dark:bg-brand-light/10 my-2"></div>

                <div className="flex justify-between items-center">
                  <span className="text-base font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">Total</span>
                  <span className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                    {formatPrice(totalPrice)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

      {/* Fixed footer button - positioned above nav bar */}
      {/* Hide for guests - they stay on success screen, no further action needed */}
      {!isGuestUser() && (
        <div className="fixed bottom-16 left-0 right-0 bg-brand-light dark:bg-brand-dark border-t border-brand-dark/10 dark:border-brand-light/10 p-6 pb-8">
          <button
            onClick={() => {
              // Reset wizard state when user completes the flow
              resetWizard();

              // Navigate based on mode using centralized routes
              if (isClientMode()) {
                // Client mode: navigate to appointments page (meus agendamentos)
                navigate(ROUTES.APPOINTMENTS);
              } else {
                // Professional mode: navigate to professional agenda
                navigate(ROUTES.PROFESSIONAL.AGENDA);
              }
            }}
            className="w-full py-5 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium uppercase tracking-widest text-sm hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
          >
            Concluir
          </button>
        </div>
      )}
    </div>
    );
  };

  // Show success screen if booking is confirmed (full-page overlay like mobile)
  if (bookingConfirmed) {
    return (
      <div className="fixed inset-0 z-[60] bg-brand-light dark:bg-brand-dark">
        {renderSuccessScreen()}
      </div>
    );
  }

  // Check if we have the required data to show confirmation screen
  if (!selectedSlot && !bookingConfirmed) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 bg-brand-light dark:bg-brand-dark">
        <div className="text-center">
          <h3 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide">Nenhum horário selecionado</h3>
          <p className="text-brand-dark/60 dark:text-brand-light/60 mb-4 tracking-wide">Volte à tela anterior para selecionar um horário.</p>
          <button
            onClick={() => goToPreviousStep()}
            className="px-6 py-3 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium uppercase tracking-widest text-sm hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto pb-32 mobile-scroll">
        <div className="px-4 py-4">
          {renderClientCard()}
          {renderDateTimeCard()}
          {renderServicesCard()}
          {renderClientNotesCard()}
          {renderImportantInfoCard()}

          {/* Bottom spacing for better scroll experience */}
          <div className="h-5"></div>
        </div>
      </div>

      {/* Booking Button */}
      {renderBookingButton()}
      
      {/* Custom Styles */}
      <style>{`
        .transition-smooth {
          transition: all 0.2s ease-in-out;
        }
        
        .spinner {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default WizardConfirmationScreen;